import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { FadeUp } from "./Motion";
import { ABSTRACT_BG } from "../data/content";
import { useContent } from "../context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { content } = useContent();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
    } catch {
      setSent(false);
    } finally {
      setSubmitting(false);
      setTimeout(() => setSent(false), 5000);
    }
  };

  return (
    <section id="contact" className="relative py-28 md:py-40 bg-black text-white overflow-hidden" data-testid="contact-section">
      <div className="pointer-events-none absolute inset-0 -z-0">
        <img src={ABSTRACT_BG} alt="" aria-hidden
          className="absolute -bottom-40 -right-40 w-[60vw] max-w-[800px] opacity-20 mix-blend-screen animate-drift-slower"
          style={{ filter: "invert(1)" }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <FadeUp>
          <div className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.32em] text-white/60">
            <span className="font-display text-white">05</span>
            <span className="h-px w-8 bg-white/40" />
            <span>Start the conversation</span>
          </div>
        </FadeUp>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
          <FadeUp delay={0.05} className="md:col-span-7">
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-light leading-[0.98] tracking-[-0.03em]">
              Let's protect <br />
              <span className="font-semibold">what you've built.</span>
            </h2>
            <p className="mt-8 max-w-md text-white/70 text-base md:text-lg leading-relaxed">
              Tell us a little about yourself. A senior advisor will personally respond within one business day — no call centers, no scripts.
            </p>
            <div className="mt-12 grid grid-cols-2 gap-8 max-w-lg">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-white/50">Call</div>
                <div className="font-display text-xl mt-2">{content.contact_phone}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-white/50">Visit</div>
                <div className="font-display text-xl mt-2">{content.contact_address}</div>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.15} className="md:col-span-5 w-full">
            <form onSubmit={handleSubmit} className="rounded-3xl bg-white text-black p-8 md:p-10" data-testid="contact-form">
              <div className="space-y-6">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Name</span>
                  <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 w-full bg-transparent border-b border-black/15 focus:border-black outline-none py-2 font-display text-lg"
                    placeholder="Your full name" data-testid="contact-name" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Email</span>
                  <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2 w-full bg-transparent border-b border-black/15 focus:border-black outline-none py-2 font-display text-lg"
                    placeholder="you@company.com" data-testid="contact-email" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">What do you need covered?</span>
                  <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-2 w-full bg-transparent border-b border-black/15 focus:border-black outline-none py-2 font-display text-lg resize-none"
                    placeholder="A few words on what you'd like to protect…" data-testid="contact-message" />
                </label>
              </div>
              <button type="submit" disabled={submitting}
                className="mt-10 w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-black text-white text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-60"
                data-testid="contact-submit">
                {sent ? "Thank you — we'll be in touch." : submitting ? "Sending…" : "Request a callback →"}
              </button>
              {sent && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="mt-4 text-center text-xs text-zinc-500" data-testid="contact-success">
                  A senior advisor will reach out within 1 business day.
                </motion.div>
              )}
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

export default Contact;
